import { z } from 'zod';
/**
 * Rate lookup and label purchase payloads.
 */
const stateCode = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Must be a 2-letter state code');
const zipCode = z
    .string()
    .trim()
    .regex(/^\d{5}(-\d{4})?$/, 'Must be a 5-digit ZIP, optionally +4');
/**
 * Optional contact details on the delivery address.
 *
 * Not required by this app, because most USPS shipments do not need them and
 * demanding a phone number for every envelope would be a tax on the common
 * case. But several carriers reject a shipment outright without them — UPS and
 * FedEx routinely, USPS for some international and express services — so the
 * fields exist, travel to the carrier, and the purchase error names them when
 * one is missing. See describeCarrierRequirement in the API.
 */
const optionalPhone = z
    .union([
    z
        .string()
        .trim()
        .min(7, 'Too short to be a phone number')
        .max(32)
        .refine((v) => (v.match(/\d/g) ?? []).length >= 7, 'Needs at least 7 digits'),
    z.literal(''),
    z.null(),
])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === null || v === '' ? null : v));
const optionalEmail = z
    .union([z.string().trim().email('Does not look like an email address').max(200), z.literal(''), z.null()])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === null || v === '' ? null : v));
export const recipientSchema = z.object({
    name: z.string().trim().min(1, 'Recipient name is required').max(120),
    phone: optionalPhone,
    email: optionalEmail,
    address1: z.string().trim().min(1, 'Street address is required').max(200),
    address2: z
        .union([z.string().trim().max(200), z.null()])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === null || v === '' ? null : v)),
    city: z.string().trim().min(1, 'City is required').max(100),
    state: stateCode,
    zip: zipCode,
    country: z.string().trim().toUpperCase().length(2).default('US'),
});
/** Inches and pounds — the units every carrier quote here uses. */
export const parcelSchema = z.object({
    weight: z.coerce.number().positive('Weight must be greater than 0').max(1000),
    length: z.coerce.number().positive().max(999),
    width: z.coerce.number().positive().max(999),
    height: z.coerce.number().positive().max(999),
});
/** Which shipping API a quote or label came from. */
export const providerSchema = z.enum(['shippo', 'easypost']);
/**
 * How USPS sees the piece: a First-Class letter, a flat (large envelope), or a
 * parcel. Only EasyPost sells letters and flats; Shippo prices everything as a
 * parcel and ignores this.
 */
export const mailClassSchema = z.enum(['LETTER', 'FLAT', 'PACKAGE']);
export const rateRequestSchema = z.object({
    recipient: recipientSchema,
    parcel: parcelSchema,
    /**
     * Which return address to quote from.
     *
     * The Base44 build quoted rates from the shop's sender address but bought the
     * label from whichever return address was selected in the dropdown. When those
     * differ the quoted price is for a shipment nobody is making — and for a
     * different origin ZIP, a different price. Quoting from the address the label
     * will actually ship from removes that gap. Falls back to the sender address
     * when omitted, which is what bulk runs use.
     */
    returnAddressId: z.string().optional(),
    /** Envelope postage is USPS-only, and the rate list is filtered to match. */
    isEnvelope: z.boolean().default(false),
    /** Letter, flat or package. Checked against the real size and weight. */
    mailClass: mailClassSchema.default('PACKAGE'),
    insuranceAmount: z.coerce.number().min(0).max(10_000).optional(),
    /**
     * Printed on the label, for carriers that support it.
     *
     * On the *rate* request, and not only on the purchase — which is where it
     * used to live, doing nothing. Shippo attaches extras to the shipment, and
     * the shipment is created when rates are quoted; an `extra` sent later with
     * the transaction is accepted and silently ignored. Verified against the
     * API: a reference set at purchase time leaves the shipment's extra empty,
     * so nothing was ever printed.
     *
     * It therefore has to be known before rating, which is why changing it
     * clears the quoted rates in the UI.
     *
     * Carriers truncate at their own limits — 30 characters for USPS, 35 for
     * UPS, 50 for most others — so the cap here is the most permissive and the
     * form warns rather than refusing.
     */
    referenceNumber: z.string().trim().max(50).optional().or(z.literal('')),
});
export const quotedRateSchema = z.object({
    /** Which API quoted it, so the purchase goes back to the same one. */
    source: providerSchema,
    /** The provider's shipment id. EasyPost needs it, as well as the rate, to buy. */
    shipmentId: z.string(),
    rateId: z.string(),
    provider: z.string(),
    serviceToken: z.string(),
    serviceName: z.string(),
    amount: z.number(),
    currency: z.string(),
    estimatedDays: z.number().nullable(),
    durationTerms: z.string().nullable(),
});
export const rateResponseSchema = z.object({
    source: providerSchema,
    shipmentId: z.string(),
    rates: z.array(quotedRateSchema),
    /** Non-blocking notes from the carrier, shown but not treated as failures. */
    warnings: z.array(z.string()),
});
export const singleLabelRequestSchema = z.object({
    recipient: recipientSchema,
    parcel: parcelSchema,
    returnAddressId: z.string().min(1, 'Select a return address'),
    /**
     * The exact rate the person chose, from a previous /api/rates call.
     *
     * Required rather than optional: buying a label needs the price that was on
     * screen when they clicked, not a fresh lookup that might come back different.
     */
    rateId: z.string().min(1, 'Select a shipping service'),
    /**
     * Which API produced that rate, and its shipment. Defaults to Shippo, whose
     * rate ids stand alone, so a page loaded before this existed still works.
     */
    source: providerSchema.default('shippo'),
    shipmentId: z.string().default(''),
    isEnvelope: z.boolean().default(false),
    mailClass: mailClassSchema.default('PACKAGE'),
    insuranceAmount: z.coerce.number().min(0).max(10_000).optional(),
    referenceNumber: z.string().trim().max(50).optional().or(z.literal('')),
    trackingEmail: z.string().trim().email().optional().or(z.literal('')),
    /**
     * Set by the client, so a resubmitted form or a double-click cannot buy two
     * labels. The server enforces uniqueness; see parcel.idempotencyKey.
     */
    idempotencyKey: z.string().min(8).max(100),
});
export const refundRequestSchema = z.object({
    transactionIds: z.array(z.string().min(1)).min(1).max(500),
});
export const addressValidationRequestSchema = z.object({
    name: z.string().trim().max(120).optional(),
    address1: z.string().trim().min(1).max(200),
    address2: z.string().trim().max(200).optional().or(z.literal('')),
    city: z.string().trim().min(1).max(100),
    state: stateCode,
    zip: zipCode,
    country: z.string().trim().toUpperCase().length(2).default('US'),
});
/** Carrier tracking pages, for turning a number into a link staff can click. */
export function trackingUrlFor(carrier, trackingNumber) {
    const name = carrier.toLowerCase();
    const number = encodeURIComponent(trackingNumber);
    if (name.includes('usps')) {
        return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${number}`;
    }
    if (name.includes('ups')) {
        return `https://www.ups.com/track?tracknum=${number}`;
    }
    if (name.includes('fedex')) {
        return `https://www.fedex.com/fedextrack/?trknbr=${number}`;
    }
    if (name.includes('dhl')) {
        return `https://www.dhl.com/en/express/tracking.html?AWB=${number}`;
    }
    return null;
}
//# sourceMappingURL=shipping.js.map